import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, MessageSquarePlus, ClipboardList, Calendar, Clock } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "";

export default function CoachSessionDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [comments, setComments] = useState([]);
  const [missions, setMissions] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [ss, cc, mm] = await Promise.all([
      api.get(`/coach/sessions/${id}`),
      api.get(`/coach/sessions/${id}/comments`),
      api.get(`/coach/missions`, { params: { session_id: id } }),
    ]);
    setS(ss.data); setComments(cc.data); setMissions(mm.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!s) return <div className="text-[#5C6561]">Chargement…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/coach/sessions/${id}`, {
        title: s.title, description: s.description, objectives: s.objectives,
        status: s.status, course_date: s.course_date,
      });
      toast.success("Séance enregistrée");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    await api.post(`/coach/sessions/${id}/comments`, { text: newComment });
    setNewComment(""); load();
    toast.success("Commentaire ajouté");
  };

  const delComment = async (cid) => {
    await api.delete(`/coach/comments/${cid}`);
    load();
  };

  return (
    <div className="max-w-3xl">
      <Link to={`/coach/apprenants/${s.learner_id}`} className="text-sm text-[#5C6561] inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour à l'apprenant
      </Link>
      <div className="text-xs uppercase tracking-wider text-[#D95D39] font-semibold">Séance {s.number}</div>
      <h1 className="font-serif-accent text-3xl mt-1">{s.title}</h1>

      <div className="dec-card p-6 mt-6 space-y-4">
        <div><Label>Titre</Label><Input data-testid="session-title-input" value={s.title || ""} onChange={(e) => setS({ ...s, title: e.target.value })} /></div>
        <div><Label>Description</Label><Textarea data-testid="session-description-input" value={s.description || ""} onChange={(e) => setS({ ...s, description: e.target.value })} /></div>
        <div><Label>Objectifs</Label><Textarea data-testid="session-objectives-input" value={s.objectives || ""} onChange={(e) => setS({ ...s, objectives: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Statut</Label>
            <Select value={s.status} onValueChange={(v) => setS({ ...s, status: v })}>
              <SelectTrigger data-testid="session-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">À faire</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="completed">Terminée</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Date du cours</Label><Input type="date" data-testid="session-date-input" value={s.course_date || ""} onChange={(e) => setS({ ...s, course_date: e.target.value })} /></div>
        </div>
        <Button data-testid="save-session-button" disabled={saving} onClick={save} className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">
          <Save className="w-4 h-4 mr-2" /> Enregistrer
        </Button>
      </div>

      <div className="dec-card p-6 mt-6">
        <div className="flex items-center gap-2 mb-3"><ClipboardList className="w-4 h-4 text-[#D95D39]" /><h2 className="font-serif-accent text-xl">Missions de cette séance</h2></div>
        {missions.length === 0 && <div className="text-sm text-[#5C6561]">Aucune mission liée à cette séance. Vous pouvez en associer une depuis l'onglet Missions.</div>}
        <div className="space-y-2">
          {missions.map((m) => (
            <div key={m.id} className="p-3 rounded-xl bg-[#F4EFEA] flex items-start gap-3" data-testid={`session-mission-${m.id}`}>
              <div className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${m.completed ? "bg-[#2A5B52]" : "bg-[#E5983B]"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{m.title}</div>
                {m.description && <div className="text-xs text-[#5C6561] mt-0.5 whitespace-pre-wrap">{m.description}</div>}
                <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-[#5C6561]">
                  <span>{m.completed ? "Terminée" : "À faire"}</span>
                  {m.publish_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Publiée le {fmtDate(m.publish_date)}</span>}
                  {m.deadline && <span className="inline-flex items-center gap-1 text-[#C04E2D]"><Clock className="w-3 h-3" /> Rendu {fmtDate(m.deadline)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dec-card p-6 mt-6 dec-side-accent">
        <h2 className="font-serif-accent text-xl mb-3">Commentaires du coach</h2>
        <div className="flex gap-2">
          <Textarea data-testid="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Ex: Excellent travail aujourd'hui !" />
          <Button data-testid="add-comment-button" onClick={addComment} className="bg-[#E5983B] hover:bg-[#D28224] text-white shrink-0">
            <MessageSquarePlus className="w-4 h-4" />
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {comments.length === 0 && <div className="text-sm text-[#5C6561]">Aucun commentaire pour le moment.</div>}
          {comments.map((c) => (
            <div key={c.id} className="p-3 rounded-xl bg-white border border-[#E6E1DA] flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-[#1F2421] whitespace-pre-wrap">{c.text}</div>
                <div className="text-[11px] text-[#5C6561] mt-1">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <Button data-testid={`delete-comment-${c.id}`} variant="ghost" size="icon" onClick={() => delComment(c.id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
